import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import ServingCommunitySection from '../../components/feature/ServingCommunitySection';

export default function IndexedUniversalLifePage() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Indexed Universal Life Insurance Indiana | IUL';

    const setMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'IUL insurance in Indiana — market-linked growth with downside protection. Lifetime coverage plus tax-deferred cash value accumulation. Free quotes from local experts.');
    setMeta('keywords', 'indexed universal life insurance, IUL insurance Indiana, market-linked life insurance, cash value life insurance, permanent life insurance Indiana');
    setMeta('og:title', 'Indexed Universal Life Insurance Indiana | IUL Policies', true);
    setMeta('og:description', 'Get market-linked growth with downside protection. IUL insurance combines lifetime coverage with tax-deferred wealth accumulation for Indiana families.', true);
    setMeta('og:image', 'https://public.readdy.ai/ai/img_res/f3d0c9fe2e61e63c200d7ecddf7f1667.jpg', true);
    setMeta('twitter:card', 'summary_large_image', false);
    setMeta('twitter:image', 'https://readdy.ai/api/search-image?query=Indiana%20financial%20advisor%20presenting%20indexed%20universal%20life%20insurance%20IUL%20growth%20chart%20with%20upside%20potential%20and%20zero%20floor%20protection%20to%20professional%20couple%20modern%20office%20clean%20bright%20background&width=1200&height=630&seq=og-iul-product&orientation=landscape', false);
    setMeta('og:url', `${SITE_URL}/indexed-universal-life`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/indexed-universal-life`);

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/indexed-universal-life#webpage`,
      "url": `${SITE_URL}/indexed-universal-life`,
      "name": "Indexed Universal Life Insurance Indiana | IUL Policies",
      "description": "Indexed Universal Life (IUL) insurance in Indiana offers market-linked growth with downside protection. Get lifetime coverage plus tax-deferred cash value accumulation.",
      "datePublished": "2024-01-15T00:00:00-05:00",
      "dateModified": new Date().toISOString(),
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`
      },
      "breadcrumb": {
        "@id": `${SITE_URL}/indexed-universal-life#breadcrumb`
      },
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": ["h1", "h2:first-of-type", ".key-takeaways-heading"]
      }
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Indexed Universal Life Insurance",
      "name": "Indexed Universal Life Insurance",
      "description": "Permanent life insurance with market-linked cash value growth and downside protection for Indiana families",
      "provider": {
        "@type": "Organization",
        "name": "Hoosier Life Insurance",
        "url": SITE_URL
      },
      "areaServed": {
        "@type": "State",
        "name": "Indiana"
      },
      "offers": {
        "@type": "AggregateOffer",
        "priceCurrency": "USD",
        "lowPrice": "150",
        "highPrice": "1000",
        "offerCount": "100"
      }
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Indexed Universal Life Insurance?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Indexed Universal Life (IUL) insurance is a type of permanent life insurance that combines a guaranteed death benefit with a cash value component. Your cash value grows based on the performance of a chosen market index — commonly the S&P 500 — but with a downside floor (typically 0%) that prevents your account from losing value during market downturns. This means you participate in market gains while your principal is always protected."
          }
        },
        {
          "@type": "Question",
          "name": "How does the cash value grow in an IUL policy?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Each policy year, your cash value is credited with interest based on how the linked market index performed. Most IUL policies set a floor of 0-1% (you never lose principal to market losses) and a cap of 10-14% (your maximum credited rate in any given year). For example, if the S&P 500 gains 18% but your cap is 12%, you receive 12%. If the index drops 25%, you receive 0% — your balance holds. This structure provides market-linked growth with downside protection that traditional savings accounts and whole life policies cannot match."
          }
        },
        {
          "@type": "Question",
          "name": "What are the main benefits of IUL insurance?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "IUL policies offer six core advantages: (1) Lifetime death benefit — coverage that never expires as long as the policy is properly funded. (2) Tax-deferred cash value growth — your gains compound without annual tax drag. (3) Market-linked returns with downside protection — growth tied to an index with a 0% floor. (4) Flexible premium payments — adjust how much you pay within policy limits based on your income. (5) Tax-free policy loans — borrow against your cash value for retirement income, college funding, or emergencies without triggering a taxable event. (6) Indiana estate planning advantage — the death benefit passes to heirs completely free of Indiana income tax."
          }
        },
        {
          "@type": "Question",
          "name": "Who should consider Indexed Universal Life insurance?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "IUL is well-suited for Indiana families and professionals in several situations: high-income earners who have maxed out their 401(k) and IRA contributions and want additional tax-advantaged growth; business owners who want key-person coverage while building a supplemental retirement fund; parents funding college savings without FAFSA impact; families doing estate planning who want the death benefit to pass tax-free to heirs; and anyone who wants permanent life insurance with more growth potential than traditional whole life. It is not ideal for people who need maximum death benefit at minimum cost — for that, term life is a better fit."
          }
        },
        {
          "@type": "Question",
          "name": "How much does IUL insurance cost in Indiana?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "IUL premiums vary based on age, health classification, coverage amount, chosen riders, and how aggressively you want to fund the cash value component. As a general reference, a healthy 35-year-old in Indianapolis might pay $300–$600 per month for $500,000 in IUL coverage — significantly more than term life, but the premium also builds cash value rather than simply paying for protection. Because we work with 20+ carriers, we can compare IUL illustrations side-by-side to find the policy with the best cap rates, lowest internal costs, and strongest carrier ratings for your specific situation. Call (317) 648-2983 for a no-obligation personalized illustration."
          }
        }
      ]
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/indexed-universal-life#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": `${SITE_URL}/`
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Indexed Universal Life",
          "item": `${SITE_URL}/indexed-universal-life`
        }
      ]
    };

    // InsuranceProduct schema
    const ipSchema = {
      '@context': 'https://schema.org',
      '@type': 'InsuranceProduct',
      '@id': `${SITE_URL}/indexed-universal-life#insurance-product`,
      name: 'Indexed Universal Life Insurance (IUL)',
      description: 'Permanent indexed universal life insurance linking cash value growth to a market index (S&P 500) with a 0% floor, guaranteeing against principal loss. Provides lifetime death benefit, tax-deferred growth, and flexible premiums for Indiana families and business owners.',
      url: `${SITE_URL}/indexed-universal-life`,
      category: 'Life Insurance',
      brand: { '@type': 'Brand', name: 'Hoosier Life Insurance' },
      provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization` },
      isRelatedTo: { '@type': 'Organization', '@id': `${SITE_URL}/#business` },
      areaServed: { '@type': 'State', name: 'Indiana' },
      offers: {
        '@type': 'Offer',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          priceCurrency: 'USD',
          description: 'Starting from $300/month for a healthy 35-year-old with $500,000 coverage',
        },
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/indexed-universal-life`,
      },
    };
    const ipScriptIUL = document.createElement('script');
    ipScriptIUL.id = 'schema-iul-insurance-product';
    ipScriptIUL.type = 'application/ld+json';
    ipScriptIUL.textContent = JSON.stringify(ipSchema);
    document.head.appendChild(ipScriptIUL);

    const schemas = [webPageSchema, serviceSchema, faqSchema, breadcrumbSchema];
    const scriptIds = ['iul-webpage-schema', 'iul-service-schema', 'iul-faq-schema', 'iul-breadcrumb-schema'];
    schemas.forEach((schema, i) => {
      let script = document.getElementById(scriptIds[i]);
      if (!script) {
        script = document.createElement('script');
        script.id = scriptIds[i];
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    });

    return () => {
      schemas.forEach((_, i) => {
        const script = document.getElementById(scriptIds[i]);
        if (script) script.remove();
      });
      const ipEl = document.getElementById('schema-iul-insurance-product');
      if (ipEl) ipEl.remove();
    };
  }, [SITE_URL]);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#F9F7F4] via-white to-orange-50 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#D97642]/10 text-[#D97642] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-line-chart-line text-lg"></i>
                Market-Linked Growth with Protection
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Indexed Universal Life Insurance — Tax-Free Growth for Indiana Families
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Get the best of both worlds: <strong>lifetime death benefit protection</strong> plus <strong>cash value growth</strong> linked to market performance—with a safety net to protect against losses.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Link to="/book-appointment" className="bg-[#D97642] hover:bg-[#C26638] text-white px-8 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                  Get Free IUL Quote
                </Link>
                <a href="tel:3176482983" className="bg-white hover:bg-gray-50 text-gray-900 px-8 py-4 rounded-lg font-semibold border-2 border-gray-300 transition-all whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  (317) 648-2983
                </a>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <i className="ri-shield-check-line text-[#D97642] text-xl"></i>
                  <span>Downside Protection</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-[#D97642] text-xl"></i>
                  <span>Tax-Deferred Growth</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-hand-coin-line text-[#D97642] text-xl"></i>
                  <span>Flexible Premiums</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20financial%20advisor%20showing%20growth%20chart%20to%20happy%20family%20in%20modern%20office%20with%20upward%20trending%20graphs%20and%20market%20index%20displays%20bright%20natural%20lighting%20clean%20minimalist%20background%20conveying%20trust%20and%20financial%20success&width=600&height=700&seq=iul-hero-001&orientation=portrait"
                alt="IUL Insurance Growth Strategy"
                title="Indexed Universal Life Insurance Indianapolis — Market-Linked Growth with Downside Protection"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width={600}
                height={700}
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Takeaways — AI SEO Signal */}
      <section className="py-8 bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-white font-bold text-lg mb-3">Key Takeaways: Indexed Universal Life Insurance in Indiana</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'IUL ties cash value growth to a stock market index (like the S&P 500) with a 0% loss floor — you can never lose cash value to market crashes',
                  'Cap rates of 10–14% allow for significantly higher returns than whole life\'s guaranteed 2–4% rate',
                  'Flexible premium payments make IUL ideal for Indiana business owners with variable income',
                  'Cash value grows tax-deferred and can be accessed tax-free through policy loans for any purpose',
                  'IUL works best for Indiana residents under 45 with a 20+ year time horizon and tolerance for complexity',
                  'Historically, IUL policies have averaged 6–8% annual returns vs. 4–5% for whole life dividends',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/90 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-white/50 text-xs">Last reviewed March 2025 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* How IUL Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How Indexed Universal Life Insurance Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              IUL combines permanent life insurance protection with a cash value account that grows based on market index performance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-[#F9F7F4] to-white p-8 rounded-xl border border-gray-200">
              <div className="w-16 h-16 bg-[#1A3A52] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-star-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Death Benefit Protection</h3>
              <p className="text-gray-700 leading-relaxed">
                Your beneficiaries receive a <strong>guaranteed death benefit</strong> when you pass away, providing financial security for your family regardless of market conditions.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#F9F7F4] to-white p-8 rounded-xl border border-gray-200">
              <div className="w-16 h-16 bg-[#D97642] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Market-Linked Growth</h3>
              <p className="text-gray-700 leading-relaxed">
                Your cash value is credited with interest based on the performance of a <strong>stock market index</strong> like the S&P 500, offering growth potential beyond traditional whole life.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#F9F7F4] to-white p-8 rounded-xl border border-gray-200">
              <div className="w-16 h-16 bg-[#1A3A52] rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Downside Protection</h3>
              <p className="text-gray-700 leading-relaxed">
                A <strong>guaranteed floor</strong> (typically 0-1%) protects your cash value from market losses. Even if the index drops, your account value won't decrease due to market performance.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-[#F9F7F4] rounded-2xl p-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">The IUL Growth Formula</h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-arrow-up-line text-[#D97642]"></i>
                  Cap Rate (Upside Limit)
                </h4>
                <p className="text-gray-700 mb-4">
                  Most policies cap annual gains at <strong>10-14%</strong>. If the S&P 500 gains 20%, you'd be credited with the cap rate (e.g., 12%).
                </p>
              </div>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-arrow-down-line text-[#1A3A52]"></i>
                  Floor Rate (Downside Protection)
                </h4>
                <p className="text-gray-700 mb-4">
                  Your floor is typically <strong>0-1%</strong>. If the market drops 30%, you'd receive 0% growth—no loss to your cash value.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-lg p-6 mt-6">
              <p className="text-gray-800 font-medium">
                <strong>Example:</strong> If the S&P 500 returns +15%, -8%, +22%, +5% over 4 years with a 12% cap and 0% floor, your credited rates would be: 12%, 0%, 12%, 5%—averaging 7.25% with zero losses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Indiana Families Choose IUL Insurance</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Indexed Universal Life offers unique advantages for wealth accumulation and legacy planning
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#F9F7F4] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-funds-line text-2xl text-[#1A3A52]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Higher Growth Potential</h3>
              <p className="text-gray-700">Historically, IUL policies have averaged <strong>6-8% annual returns</strong>—significantly higher than whole life's typical 4-5% dividends.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#F9F7F4] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-hand-coin-line text-2xl text-[#1A3A52]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Flexible Premium Payments</h3>
              <p className="text-gray-700">Adjust your premium payments up or down based on your financial situation—ideal for business owners with variable income.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#F9F7F4] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-file-shield-2-line text-2xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Tax-Free Policy Loans</h3>
              <p className="text-gray-700">Borrow against your cash value <strong>tax-free</strong> for any purpose—college tuition, business investments, or retirement income.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#F9F7F4] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-[#1A3A52]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Lifetime Coverage</h3>
              <p className="text-gray-700">Unlike term life, IUL provides <strong>permanent protection</strong> that lasts your entire life as long as premiums are paid.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#F9F7F4] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Tax-Deferred Growth</h3>
              <p className="text-gray-700">Your cash value grows <strong>tax-deferred</strong>—you don't pay taxes on gains until you withdraw them, maximizing compound growth.</p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="w-14 h-14 bg-[#F9F7F4] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-parent-line text-2xl text-[#1A3A52]"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Estate Planning Tool</h3>
              <p className="text-gray-700">Death benefits pass to beneficiaries <strong>income tax-free</strong>, making IUL an efficient wealth transfer vehicle for Indiana families.</p>
            </div>
          </div>
        </div>
      </section>

      {/* IUL vs Other Policies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">IUL vs. Other Life Insurance Types</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">See how Indexed Universal Life compares to term life and whole life insurance</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-[#1A3A52] text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Feature</th>
                  <th className="px-6 py-4 text-left font-bold">Term Life</th>
                  <th className="px-6 py-4 text-left font-bold">Whole Life</th>
                  <th className="px-6 py-4 text-left font-bold bg-[#0F2A3F]">Indexed Universal Life</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Coverage Duration</td>
                  <td className="px-6 py-4 text-gray-700">10-30 years</td>
                  <td className="px-6 py-4 text-gray-700">Lifetime</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Lifetime</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Cash Value</td>
                  <td className="px-6 py-4 text-gray-700">None</td>
                  <td className="px-6 py-4 text-gray-700">Yes (4-5% growth)</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Yes (6-8% potential)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Premium Flexibility</td>
                  <td className="px-6 py-4 text-gray-700">Fixed</td>
                  <td className="px-6 py-4 text-gray-700">Fixed</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Flexible</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Market Participation</td>
                  <td className="px-6 py-4 text-gray-700">No</td>
                  <td className="px-6 py-4 text-gray-700">No</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Yes (with protection)</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Downside Protection</td>
                  <td className="px-6 py-4 text-gray-700">N/A</td>
                  <td className="px-6 py-4 text-gray-700">Guaranteed</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Guaranteed Floor</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Tax-Free Loans</td>
                  <td className="px-6 py-4 text-gray-700">No</td>
                  <td className="px-6 py-4 text-gray-700">Yes</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Yes</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Best For</td>
                  <td className="px-6 py-4 text-gray-700">Temporary needs</td>
                  <td className="px-6 py-4 text-gray-700">Conservative savers</td>
                  <td className="px-6 py-4 text-[#D97642] font-semibold">Growth-focused families</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Who Should Consider IUL */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Is IUL Right for You?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Indexed Universal Life is ideal for specific financial situations and goals</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#D97642]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-2xl text-[#D97642]"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Great Fit If You:</h3>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  'Want <strong>permanent life insurance</strong> with growth potential beyond whole life',
                  'Are comfortable with <strong>market-linked returns</strong> (with downside protection)',
                  'Need <strong>flexible premium payments</strong> due to variable income',
                  'Want to build <strong>tax-deferred wealth</strong> for retirement or legacy planning',
                  'Are a <strong>business owner</strong> seeking tax-advantaged savings beyond 401(k) limits',
                  'Have maxed out other retirement accounts and want additional <strong>tax-free income</strong> options',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-[#D97642] text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-2xl text-orange-600"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">May Not Be Ideal If You:</h3>
                </div>
              </div>
              <ul className="space-y-4">
                {[
                  'Only need <strong>temporary coverage</strong> (term life is more affordable)',
                  'Have a <strong>tight budget</strong> and can\'t afford higher premiums than term life',
                  'Want <strong>guaranteed cash value growth</strong> with zero market exposure (choose whole life)',
                  'Don\'t understand how <strong>caps and floors</strong> work and prefer simpler products',
                  'Are looking for a <strong>short-term investment</strong> (IUL is designed for long-term wealth building)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-arrow-right-s-line text-orange-600 text-xl flex-shrink-0 mt-1"></i>
                    <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] rounded-2xl p-10 text-white text-center">
            <h3 className="text-3xl font-bold mb-4">Not Sure If IUL Is Right for You?</h3>
            <p className="text-xl mb-8 text-white/90">
              Schedule a free consultation with our Indiana-licensed agents. We'll review your financial goals and recommend the best policy type for your situation.
            </p>
            <Link to="/book-appointment" className="inline-block bg-[#D97642] text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* ── Related Services ── */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Explore More</span>
            <h2 className="text-3xl font-bold text-[#1A3A52] mb-3">Related Coverage &amp; Wealth Strategies</h2>
            <p className="text-gray-600 max-w-xl mx-auto">IUL pairs well with these products — explore your full financial protection plan.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { to: '/universal-life', icon: 'ri-shield-star-line', title: 'Universal Life Insurance', desc: 'Flexible permanent coverage with adjustable premiums and guaranteed interest growth.' },
              { to: '/whole-life', icon: 'ri-shield-check-line', title: 'Whole Life Insurance', desc: 'Prefer guaranteed cash value growth with no market exposure? Whole life may be the better fit.' },
              { to: '/wealth-building', icon: 'ri-funds-box-line', title: '6-Step Wealth-Building System', desc: 'See how IUL fits into our proven wealth accumulation strategy for Indiana families.' },
              { to: '/term-vs-whole-life', icon: 'ri-scales-line', title: 'Compare Term vs. Whole Life', desc: 'Still deciding? See how IUL compares to term and whole life insurance.' },
            ].map((item) => (
              <Link key={item.to} to={item.to} className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#D97642]/40 hover:shadow-lg transition-all cursor-pointer">
                <div className="w-12 h-12 bg-[#1A3A52]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#D97642] transition-colors">
                  <i className={`${item.icon} text-2xl text-[#1A3A52] group-hover:text-white`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#1A3A52] mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{item.desc}</p>
                <span className="flex items-center text-[#D97642] font-semibold text-sm">
                  Learn More <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Not sure which product is right for you?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/quiz" className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-semibold rounded-lg hover:bg-[#C26638] transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                <i className="ri-questionnaire-line mr-2 text-xl"></i>
                Take the 60-Second Quiz
              </Link>
              <Link to="/instant-quote" className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-gray-300 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition-all whitespace-nowrap">
                <i className="ri-calculator-line mr-2 text-xl"></i>
                Get a Free Instant Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — always-visible for crawlers; schema mirrors this content */}
      <section className="py-20 bg-white" id="faq">
        <div className="max-w-[1100px] mx-auto px-6">
          <span className="text-[12px] font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4">Indexed Universal Life Insurance — Common Questions</h2>
          <p className="text-[#4A5568] mb-10 max-w-2xl text-sm leading-relaxed">
            Indiana families and business owners most often ask these questions before purchasing an IUL policy. Here are straightforward answers from our licensed agents — no jargon, no pressure.
          </p>
          <div className="space-y-8">
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-lg font-bold text-[#1A3A52] mb-3">What is Indexed Universal Life Insurance?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">
                Indexed Universal Life (IUL) insurance is a type of permanent life insurance that combines a guaranteed death benefit with a cash value component. Your cash value grows based on the performance of a chosen market index — commonly the S&P 500 — but with a downside floor (typically 0%) that prevents your account from losing value during market downturns. This means you participate in market gains while your principal is always protected.
              </p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-lg font-bold text-[#1A3A52] mb-3">How does the cash value grow in an IUL policy?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">
                Each policy year, your cash value is credited with interest based on how the linked market index performed. Most IUL policies set a floor of 0-1% (you never lose principal to market losses) and a cap of 10-14% (your maximum credited rate in any given year). For example, if the S&P 500 gains 18% but your cap is 12%, you receive 12%. If the index drops 25%, you receive 0% — your balance holds. This structure provides market-linked growth with downside protection that traditional savings accounts and whole life policies cannot match.
              </p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-lg font-bold text-[#1A3A52] mb-3">What are the main benefits of IUL insurance?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">
                IUL policies offer six core advantages: (1) <strong>Lifetime death benefit</strong> — coverage that never expires as long as the policy is properly funded. (2) <strong>Tax-deferred cash value growth</strong> — your gains compound without annual tax drag. (3) <strong>Market-linked returns with downside protection</strong> — growth tied to an index with a 0% floor. (4) <strong>Flexible premium payments</strong> — adjust how much you pay within policy limits based on your income. (5) <strong>Tax-free policy loans</strong> — borrow against your cash value for retirement income, college funding, or emergencies without triggering a taxable event. (6) <strong>Indiana estate planning advantage</strong> — the death benefit passes to heirs completely free of Indiana income tax.
              </p>
            </div>
            <div className="border-b border-gray-100 pb-8">
              <h3 className="text-lg font-bold text-[#1A3A52] mb-3">Who should consider Indexed Universal Life insurance?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">
                IUL is well-suited for Indiana families and professionals in several situations: high-income earners who have maxed out their 401(k) and IRA contributions and want additional tax-advantaged growth; business owners who want key-person coverage while building a supplemental retirement fund; parents funding college savings without FAFSA impact; families doing estate planning who want the death benefit to pass tax-free to heirs; and anyone who wants permanent life insurance with more growth potential than traditional whole life. It is not ideal for people who need maximum death benefit at minimum cost — for that, term life is a better fit.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#1A3A52] mb-3">How much does IUL insurance cost in Indiana?</h3>
              <p className="text-[#4A5568] text-sm leading-relaxed">
                IUL premiums vary based on age, health classification, coverage amount, chosen riders, and how aggressively you want to fund the cash value component. As a general reference, a healthy 35-year-old in Indianapolis might pay $300–$600 per month for $500,000 in IUL coverage — significantly more than term life, but the premium also builds cash value rather than simply paying for protection. Because we work with 20+ carriers, we can compare IUL illustrations side-by-side to find the policy with the best cap rates, lowest internal costs, and strongest carrier ratings for your specific situation. Call (317) 648-2983 for a no-obligation personalized illustration.
              </p>
            </div>
            <div className="pt-5 border-t border-gray-100 mt-2">
              <p className="text-sm text-[#4A5568]">
                Comparing IUL to term life or whole life?{' '}
                <Link to="/compare" className="text-[#D97642] font-semibold hover:underline whitespace-nowrap">See all policy types side-by-side &rarr;</Link>
              </p>
            </div>
          </div>
          <div className="mt-12 text-center">
            <Link to="/compare" className="inline-block bg-[#D97642] text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap">
              Compare IUL vs Other Policies
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F]">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Ready to Explore IUL Insurance?</h2>
          <p className="text-xl mb-10 text-white/90 leading-relaxed">
            Get a personalized quote from Indiana-licensed agents who specialize in Indexed Universal Life insurance. We'll help you understand if IUL is the right fit for your financial goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-appointment" className="bg-[#D97642] text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#C26638] transition-all shadow-lg whitespace-nowrap">
              Get Free IUL Quote
            </Link>
            <a href="tel:3176482983" className="bg-white/10 hover:bg-white/20 text-white px-10 py-4 rounded-lg font-bold text-lg transition-all border border-white/30 whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>
              (317) 648-2983
            </a>
          </div>
          <p className="mt-8 text-sm text-white/60">
            Serving Indianapolis, Fort Wayne, Carmel, Fishers, and all of Indiana
          </p>
        </div>
      </section>

      <ServingCommunitySection productName="Indexed Universal Life Insurance" productSlug="indexed-universal-life" />
      <Footer />
    </div>
  );
}